/**
 * Which card is the viewer looking at, and have they stopped scrolling?
 *
 * Ticket 86d40p593. Touch has no hover, so a card cannot know it is being pointed at. What it
 * can know is that it is in the middle of the screen and the viewer has stopped moving — which
 * is the same signal, expressed the way a phone expresses it.
 *
 * ONE LISTENER FOR THE WHOLE PAGE. A library grid holds dozens of cards; forty scroll handlers
 * firing on every frame is how a phone's scroll turns to treacle. This module keeps a single
 * passive listener and a single timer, and notifies subscribers only when the state actually
 * changes.
 *
 * `scrolling` is deliberately not the inverse of "idle": it goes true on the first scroll event
 * and only returns to false once nothing has moved for SETTLE_MS. A momentum scroll on iOS
 * fires events in bursts with gaps between them, so a naive "no event this frame" test would
 * flap on and off several times during one flick.
 */

/** How long the page must be still before we call it settled. */
const SETTLE_MS = 350;

type Listener = (scrolling: boolean) => void;

const listeners = new Set<Listener>();
let scrolling = false;
let timer: number | null = null;
let attached = false;

function notify() {
  for (const l of listeners) l(scrolling);
}

function onScroll() {
  if (!scrolling) {
    scrolling = true;
    notify();
  }
  if (timer !== null) window.clearTimeout(timer);
  timer = window.setTimeout(() => {
    scrolling = false;
    timer = null;
    notify();
  }, SETTLE_MS);
}

/**
 * Subscribe to scroll-settled state. Returns an unsubscribe function.
 *
 * The listener is attached on the first subscriber and removed on the last, so a page with no
 * cards carries no handler at all.
 */
export function onScrollSettle(listener: Listener): () => void {
  listeners.add(listener);
  if (!attached && typeof window !== 'undefined') {
    // `passive` matters: without it the browser must wait for the handler before it can
    // scroll, which is exactly the jank this module exists to avoid.
    window.addEventListener('scroll', onScroll, { passive: true });
    attached = true;
  }
  // Tell the new subscriber where things stand rather than leaving it to guess.
  listener(scrolling);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && attached) {
      window.removeEventListener('scroll', onScroll);
      attached = false;
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
      scrolling = false;
    }
  };
}

/**
 * Is this element close enough to the middle of the screen to be "the one being looked at"?
 *
 * A band rather than a point: requiring the exact centre would make the state flicker as a
 * finger settles, and would often match nothing at all between two cards.
 */
export function isNearViewportCentre(el: Element, bandFraction = 0.25): boolean {
  if (typeof window === 'undefined') return false;
  const rect = el.getBoundingClientRect();
  const viewportCentre = window.innerHeight / 2;
  const elementCentre = rect.top + rect.height / 2;
  const band = window.innerHeight * bandFraction;
  return Math.abs(elementCentre - viewportCentre) <= band / 2;
}

/** Touch-shaped device: no hover available, so the scroll-focus behaviour applies. */
export function isTouchLike(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return !window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

/**
 * ONE PREVIEW AT A TIME, ACROSS THE WHOLE PAGE.
 *
 * The centre band is a quarter of the viewport, so on a short card two can satisfy it at once.
 * Without this they both arm, both play, and the viewer hears two clips over each other — the
 * teasers carry audio, so this is not a subtle glitch.
 *
 * A claim rather than a lock: whoever claims last wins and the previous holder is told to
 * stop. That is the right precedence for scrolling, where the newly centred card is the one
 * being looked at.
 */
type Release = () => void;

/**
 * Speedies (F-129) extend the slot to hold up to 2 MUTED previews (feed tiles, the Speedy tab, the
 * composer list), while a preview with sound still plays alone. The rule, exactly:
 *   * a claim with no `capacity` (or 1) has sound: it evicts every holder and holds alone, as
 *     before Speedies;
 *   * a muted claim (`capacity` 2+) is refused while a holder with sound is playing;
 *   * a muted claim joins while there are fewer muted holders than its `capacity`; otherwise it
 *     evicts the oldest muted holder, or, with `evict: false`, is refused and takes nothing.
 * A holder that already holds the slot gets `false` and nothing changes, as before.
 */
interface Holder {
  holder: symbol;
  release: Release;
  withSound: boolean;
}

/** Oldest claim first. */
let holders: Holder[] = [];

function evict(h: Holder): void {
  holders = holders.filter((x) => x !== h);
  // Wrapped because a throw here must not leave the slot in a half state while a video is still
  // playing. The whole point of this is that a clip with sound cannot start over another one, and
  // a failed cleanup is not a reason to abandon that guarantee.
  try {
    h.release();
  } catch (err) {
    console.warn('[preview] evicting the previous preview failed', err);
  }
}

/**
 * Claim a playback slot. Returns false if this holder already has one, or if a muted claim can't
 * be granted (see above). An evicted holder's release callback runs before the claim is granted.
 */
export function claimPreviewSlot(
  holder: symbol,
  onEvicted: Release,
  opts?: { capacity?: number; evict?: boolean },
): boolean {
  if (holders.some((h) => h.holder === holder)) return false;
  const capacity = Math.max(1, opts?.capacity ?? 1);

  if (capacity === 1) {
    for (const h of [...holders]) evict(h);
    holders.push({ holder, release: onEvicted, withSound: true });
    return true;
  }

  if (holders.some((h) => h.withSound)) return false;
  if (holders.length >= capacity) {
    if (opts?.evict === false) return false;
    evict(holders[0]);
  }
  holders.push({ holder, release: onEvicted, withSound: false });
  return true;
}

/** Give the slot up. Safe to call when not holding it. */
export function releasePreviewSlot(holder: symbol): void {
  holders = holders.filter((h) => h.holder !== holder);
}

/** Test seam: how many previews hold a slot now. */
export function previewSlotHolderCount(): number {
  return holders.length;
}

/**
 * EVERY CARD STILL ON THE PAGE, NOT JUST THIS ONE.
 *
 * A per-card gate is not enough. A centred card whose own image happens to have arrived can
 * still start pulling a clip while cards further down the grid are queued behind it for the
 * same host — and the browser's per-host budget is shared, so the teaser wins and the rest of
 * the grid sits on blurhash. Reported on iPhone 24 Sep: placeholders everywhere, some images
 * giving up entirely (MediaImage has no retry — one failed request and the card shows its
 * fallback for good).
 *
 * So no preview may fetch until the page has finished fetching its pictures.
 *
 * Stills are found by their container: `LessonPreview` marks its host with `data-centred`, so
 * `[data-centred] img` is exactly the set of card images and nothing else. Images added later
 * by pagination are picked up because the check re-runs on every load.
 */
export function whenAllStillsLoaded(run: () => void): () => void {
  if (typeof document === 'undefined') {
    run();
    return () => {};
  }

  const pending = () =>
    Array.from(document.querySelectorAll<HTMLImageElement>('[data-centred] img'))
      .filter((img) => !(img.complete && img.naturalWidth > 0));

  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    cleanup();
    run();
  };

  const check = () => {
    if (pending().length === 0) finish();
  };

  let watched: HTMLImageElement[] = [];
  const cleanup = () => {
    for (const img of watched) {
      img.removeEventListener('load', onAny);
      img.removeEventListener('error', onAny);
    }
    watched = [];
    if (timer !== null) window.clearTimeout(timer);
  };

  function onAny() {
    // Re-watch rather than assume the set is stable: a load can reveal newly rendered cards.
    cleanup();
    if (done) return;
    watch();
    check();
  }

  function watch() {
    watched = pending();
    for (const img of watched) {
      img.addEventListener('load', onAny, { once: true });
      // An image that ERRORS still counts as settled. MediaImage shows its fallback and will
      // not retry, so waiting on it forever would disable previews for the whole page.
      img.addEventListener('error', onAny, { once: true });
    }
  }

  // A hard ceiling. A stalled image must not hold the feature hostage indefinitely — on a
  // degraded network some requests simply never resolve either way.
  let timer: number | null = window.setTimeout(finish, 10_000);

  watch();
  check();
  return cleanup;
}
